import { useState } from 'react';
import { Alert, Button, Paper, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  useCompanyControllerGetCompanyQuery,
  type AgentDto,
  type CampaignDto,
  type ToolDto,
} from '@shared/services/apiService';
import {
  getRemovedAgentSettings,
  getRemovedSettingsActionMessage,
} from '@shared/utils/removedAgentSettings';
import { useEditAgentTools } from '@features/Admin/Agent/hooks/useEditAgentTools';
import AgentAdvancedSettingsModal from '@pages/Admin/Main/Agent/ui/components/AgentAdvancedSettingsModal';

export const AgentToolsWorkspace = ({
  campaign,
  agents,
  tools,
  locked,
  refetch,
}: {
  campaign: CampaignDto;
  agents: AgentDto[];
  tools: ToolDto[];
  locked: boolean;
  refetch: () => void;
}) => {
  const [selected, setSelected] = useState<AgentDto | null>(null);
  const {
    currentData: company,
    isError: companyError,
    isFetching: companyFetching,
  } = useCompanyControllerGetCompanyQuery(
    { id: campaign.companyId },
    { refetchOnMountOrArgChange: true },
  );
  const { t } = useTranslation();
  const removed = selected
    ? getRemovedSettingsActionMessage(getRemovedAgentSettings(selected, company), t)
    : null;
  const editor = useEditAgentTools({
    campaignId: campaign.id,
    squadId: campaign.squad?.id,
    agent: selected,
    blockedReason:
      companyError || companyFetching || !company
        ? 'Current company settings are unavailable.'
        : locked
          ? 'Editing is locked for your current permissions or campaign state.'
          : removed,
    onSuccess: () => {
      refetch();
      setSelected(null);
    },
  });
  return (
    <Stack gap={2}>
      {locked && (
        <Alert severity="info">
          Tool changes are locked for your current role or campaign state.
        </Alert>
      )}
      {agents.map((agent) => (
        <Paper variant="outlined" key={agent.id} sx={{ p: 2 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Typography>{agent.name}</Typography>
            <Button onClick={() => setSelected(agent)}>
              {locked ? 'View agent tools' : 'Configure agent tools'}
            </Button>
          </Stack>
        </Paper>
      ))}
      {selected && (
        <AgentAdvancedSettingsModal
          open
          onClose={() => setSelected(null)}
          agent={selected}
          agents={agents}
          campaignId={campaign.id}
          campaignTools={tools}
          campaignName={campaign.title}
          companyName={company?.name ?? ''}
          formMethods={editor.formMethods}
          onSave={editor.onSubmit}
          refetch={refetch}
          isReadOnly={locked || !!removed || companyError || companyFetching || !company}
        />
      )}
    </Stack>
  );
};
